import { Trans } from '@i18n';
import { Checkbox, FormControlLabel, FormGroup, Stack } from '@mui/material';
import { custom_field_entity_enum } from '@predium/enums';
import isEqual from 'lodash/isEqual';
import { memo, useCallback } from 'react';
import { useBuildingFilters } from '@/components/building-filters/BuildingFiltersProvider';
import { findCustomFieldFilter, upsertCustomFieldFilter } from '@/components/building-filters/custom-field-filters';
import Collapsible from '@/components/common/Collapsible';

type Props = {
  entity: custom_field_entity_enum;
  fieldKey: string;
  title: string;
};

function BooleanFilter({ entity, fieldKey, title }: Props) {
  const { filters, setFilters } = useBuildingFilters();

  const value = findCustomFieldFilter(filters.customFields, entity, fieldKey)?.equals ?? null;

  const handleToggle = useCallback(
    (toggledValue: boolean, isChecked: boolean) => {
      void setFilters((prev) => ({
        ...prev,
        customFields: upsertCustomFieldFilter(prev.customFields, entity, fieldKey, {
          equals: isChecked ? toggledValue : null,
        }),
      }));
    },
    [entity, fieldKey, setFilters],
  );

  return (
    <Collapsible title={title}>
      <Stack spacing={1} mt={-1}>
        <FormGroup
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 1,
            width: '100%',
          }}
        >
          <FormControlLabel
            control={<Checkbox checked={value === true} onChange={(e) => handleToggle(true, e.target.checked)} />}
            label={<Trans i18nKey="General_Yes" />}
          />
          <FormControlLabel
            control={<Checkbox checked={value === false} onChange={(e) => handleToggle(false, e.target.checked)} />}
            label={<Trans i18nKey="General_No" />}
          />
        </FormGroup>
      </Stack>
    </Collapsible>
  );
}

export const CustomFieldBooleanFilter = memo(BooleanFilter, isEqual);
